import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type ParamFormat = [string, string];

type CreateSearchParamsArgs =
  | string
  | Record<string, string | string[]>
  | ParamFormat[]
  | URLSearchParams;

const createSearchParams = (qs: CreateSearchParamsArgs = ''): URLSearchParams => {
  return new URLSearchParams(
    typeof qs === 'string' || Array.isArray(qs) || qs instanceof URLSearchParams
      ? qs
      : Object.keys(qs).reduce((params, key) => {
        const value = qs[key];
        return params.concat(Array.isArray(value) ? value.map((v) => [key, v]) : [[key, value]]);
      }, [] as ParamFormat[])
  );
};

export const useSearchParams = (): [URLSearchParams, (nextParams: CreateSearchParamsArgs) => void] => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(() => {
    return createSearchParams(location.search);
  }, [location.search]);

  const setSearchParams = useCallback(
    (nextParams: CreateSearchParamsArgs) => {
      const newSearchParams = createSearchParams(nextParams).toString();
      navigate('?' + newSearchParams);
    },
    [navigate]
  );

  return [searchParams, setSearchParams];
};
